/*
 * /timeline-events/[timelineEventId] (page)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
	Badge,
	Button,
	ConfirmDialog,
	ConfirmDialogAction,
	ConfirmDialogCancel,
	ConfirmDialogContent,
	ConfirmDialogDescription,
	ConfirmDialogFooter,
	ConfirmDialogHeader,
	ConfirmDialogTitle,
	ConfirmDialogTrigger,
	Container,
	Divider,
	Page,
	PageHeader,
	Paper,
	Skeleton,
} from "@psychology/react-ui"
import { ArrowLeft, History, Pencil, Trash2 } from "lucide-react"
import { TimelineEventForm } from "../../features/timeline-events/components/timeline-event-form"
import { trpc } from "../../trpc"
import { authPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/timeline-events/$timelineEventId")({
	component: TimelineEventDetailPage,
	loader: authPageLoader,
})

function TimelineEventDetailPage() {
	const { timelineEventId } = Route.useParams()
	const { user } = Route.useLoaderData()
	const navigate = useNavigate()
	const utils = trpc.useUtils()

	const {
		data: event,
		isLoading,
		refetch,
	} = trpc.timelineEvent.getByFilter.useQuery({
		where: { id: timelineEventId },
	})

	const deleteMutation = trpc.timelineEvent.delete.useMutation({
		onSuccess: async () => {
			await utils.timelineEvent.getAllByFilter.invalidate()
			navigate({ to: "/timeline-events" })
		},
	})

	if (isLoading) {
		return (
			<Page centerVertical={false}>
				<Container width={"xl"} padding={"lg"} gap={"sm"}>
					<Skeleton className={"h-8 w-48"} />
					<Divider />
					<div className={"flex flex-col gap-4"}>
						<Skeleton className={"h-24 w-full"} />
						<Skeleton className={"h-20 w-full"} />
					</div>
				</Container>
			</Page>
		)
	}

	if (!event) {
		return (
			<Page centerVertical={false}>
				<Container width={"xl"} padding={"lg"} gap={"sm"}>
					<PageHeader
						title={"Event not found"}
						left={{
							href: "/timeline-events",
							label: "Timeline",
							Icon: <ArrowLeft size={20} />,
						}}
					/>
					<Divider />
					<p className={"text-muted-foreground text-sm"}>
						{"This timeline event could not be found."}
					</p>
				</Container>
			</Page>
		)
	}

	return (
		<Page centerVertical={false}>
			<Container width={"xl"} padding={"lg"} gap={"sm"}>
				<PageHeader
					title={event.title}
					left={{
						href: "/timeline-events",
						label: "Timeline",
						Icon: <ArrowLeft size={20} />,
					}}
					right={{
						Component: (
							<div className={"flex items-center justify-end gap-2"}>
								<TimelineEventForm
									mode={"edit"}
									userId={user.id}
									event={event}
									onSuccess={() => refetch()}
									trigger={
										<Button size={"sm"} variant={"outline"}>
											<Pencil size={14} className={"mr-1.5"} />
											{"Edit"}
										</Button>
									}
								/>
								<ConfirmDialog>
									<ConfirmDialogTrigger asChild>
										<Button
											size={"sm"}
											variant={"outline"}
											className={"text-destructive hover:text-destructive"}
										>
											<Trash2 size={14} className={"mr-1.5"} />
											{"Remove"}
										</Button>
									</ConfirmDialogTrigger>
									<ConfirmDialogContent>
										<ConfirmDialogHeader>
											<ConfirmDialogTitle>
												{"Remove timeline event?"}
											</ConfirmDialogTitle>
											<ConfirmDialogDescription>
												{`This will permanently remove "${event.title}". This cannot be undone.`}
											</ConfirmDialogDescription>
										</ConfirmDialogHeader>
										<ConfirmDialogFooter>
											<ConfirmDialogCancel>{"Cancel"}</ConfirmDialogCancel>
											<ConfirmDialogAction
												onClick={() => deleteMutation.mutate({ id: event.id })}
											>
												{"Remove"}
											</ConfirmDialogAction>
										</ConfirmDialogFooter>
									</ConfirmDialogContent>
								</ConfirmDialog>
							</div>
						),
					}}
				/>
				<Divider />

				<div className={"flex flex-col gap-4"}>
					<Paper className={"flex flex-col gap-4 p-5"}>
						<div className={"flex items-center gap-3"}>
							<div
								className={
									"bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
								}
							>
								<History size={22} className={"text-muted-foreground"} />
							</div>
							<div className={"flex flex-col gap-1"}>
								<div className={"flex flex-wrap items-center gap-2"}>
									<Badge variant={"secondary"} className={"capitalize"}>
										{event.category}
									</Badge>
									{event.emotionalImpact != null && (
										<Badge variant={"outline"}>
											{`Impact: ${event.emotionalImpact}/5`}
										</Badge>
									)}
								</div>
								<p className={"text-muted-foreground text-sm"}>
									{new Date(event.date).toLocaleDateString(undefined, {
										year: "numeric",
										month: "long",
										day: "numeric",
									})}
								</p>
							</div>
						</div>
					</Paper>

					{event.description && (
						<Paper className={"flex flex-col gap-3 p-5"}>
							<h3
								className={
									"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
								}
							>
								{"Description"}
							</h3>
							<p className={"text-sm whitespace-pre-wrap"}>
								{event.description}
							</p>
						</Paper>
					)}

					{event.notes && (
						<Paper className={"flex flex-col gap-3 p-5"}>
							<h3
								className={
									"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
								}
							>
								{"Notes"}
							</h3>
							<p className={"text-sm whitespace-pre-wrap"}>{event.notes}</p>
						</Paper>
					)}
				</div>
			</Container>
		</Page>
	)
}
